/* Centralized professional textual content of the AEP report,
   reproduced from the Sollute Ocupacional reference document. */

export const AEP_INTRO = {
  introducao: `O presente documento consolida as avaliações psicossociais realizadas pela metodologia Sollute Ocupacional, com finalidade de subsidiar a gestão dos riscos ocupacionais no âmbito do Programa de Gerenciamento de Riscos – PGR. A abordagem foi estruturada em formato de Avaliação Ergonômica Preliminar – AEP, observando os princípios de identificação de perigos, avaliação de riscos e definição de medidas de prevenção previstos na NR-01, bem como a análise da organização do trabalho, condições de trabalho e fatores relacionados à ergonomia previstos na NR-17.

A avaliação de fatores psicossociais não representa diagnóstico clínico individual, tampouco substitui avaliação médica, psicológica ou análise ergonômica aprofundada quando indicada. Seus resultados devem ser interpretados em conjunto com entrevistas, observações de campo, indicadores ocupacionais, absenteísmo, rotatividade, afastamentos, denúncias, incidentes, condições ambientais, organização das jornadas e evidências documentais.`,
  objetivo: `Identificar, consolidar e classificar fatores de riscos psicossociais relacionados ao trabalho, fornecendo base técnica para priorização de ações preventivas, integração ao inventário de riscos do PGR e planejamento de medidas de controle organizacionais, administrativas e individuais.`,
  metodologia: `A metodologia adotada contempla a importação e consolidação de avaliações individuais emitidas pela aplicação Sollute NR-01, agrupamento dos dados por GHE, setor, função ou critério definido pelo responsável técnico, apuração de percentual médio de risco, contagem de respostas críticas, categorização por dimensões psicossociais e classificação por matriz de risco inspirada em critérios de probabilidade e gravidade.`,
  metodologia_passos: [
    "Levantamento das informações da empresa, unidade, função, setor e GHE.",
    "Importação dos PDFs individuais e revisão manual dos campos reconhecidos.",
    "Consolidação estatística dos percentuais de risco e respostas críticas.",
    "Análise por dimensão psicossocial e classificação por prioridade.",
    "Geração de plano de ação cronológico com medidas preventivas e evidências esperadas.",
  ],
  avaliacao: `Foram considerados fatores relacionados a demandas e carga de trabalho, autonomia, clareza de papéis, liderança, apoio da supervisão, relacionamentos, respeito no trabalho, reconhecimento, justiça organizacional, comunicação, participação, mudanças, violência, assédio, eventos críticos, condições ambientais e organização do trabalho.`,
  categorizacao: `Os fatores foram categorizados por dimensão psicossocial e por grupo de trabalhadores, permitindo identificar exposições predominantes, áreas prioritárias e necessidade de medidas preventivas integradas. A categorização deve ser revisada pelo responsável técnico sempre que houver mudanças organizacionais relevantes, alteração de processo, aumento de jornada, conflitos, denúncias ou evidências de adoecimento relacionado ao trabalho.`,
  probabilidade: `A probabilidade é estimada com base no percentual médio de risco consolidado, recorrência das respostas críticas, número de trabalhadores avaliados e abrangência do fator no grupo analisado. Para fins operacionais, são adotados níveis de P1 a P4, variando de ocorrência improvável/baixa até ocorrência frequente ou fortemente evidenciada.`,
  gravidade: `A gravidade considera o potencial de impacto sobre saúde, segurança, desempenho, clima organizacional, risco de conflitos, possibilidade de afastamentos e presença de situações críticas como violência, assédio, ameaça, sobrecarga intensa, jornadas excessivas ou falhas severas de gestão. A escala varia de S1 a S4.`,
  classificacao: `A classificação final resulta da combinação entre probabilidade e gravidade, com enquadramento em Baixo, Moderado, Alto ou Crítico. Essa classificação orienta a prioridade do plano de ação e não dispensa validação técnica por meio de observação de campo, análise documental e consulta aos trabalhadores ou lideranças.`,
  controle: `O controle dos riscos psicossociais deve priorizar medidas de caráter coletivo e organizacional, atuando sobre a origem do risco. Quando necessário, medidas administrativas e individuais podem complementar o controle, desde que não substituam intervenções sobre a organização do trabalho.`,
  implantacao: `As medidas de prevenção devem possuir responsável definido, prazo, evidências de implantação, indicadores de acompanhamento e reavaliação periódica. Recomenda-se que as ações sejam incorporadas ao plano de ação do PGR e acompanhadas em reuniões gerenciais, CIPA/CIPATR, SESMT, RH ou estrutura equivalente da empresa.`,
  coletivas: [
    "Revisão de metas, ritmos, dimensionamento de equipe e distribuição das demandas.",
    "Melhoria da comunicação interna, participação dos trabalhadores e previsibilidade das mudanças.",
    "Capacitação de lideranças em gestão respeitosa, prevenção de assédio e organização do trabalho.",
    "Implantação de canais de escuta, tratamento de conflitos e protocolos contra violência e assédio.",
  ],
  administrativas: [
    "Formalização de procedimentos, registros de orientação, fluxos de encaminhamento e acompanhamento de indicadores.",
    "Controle de jornada, pausas, horas extras, rodízios e priorização das demandas críticas.",
    "Registro de treinamentos, reuniões, investigações, auditorias internas e evidências do plano de ação.",
  ],
  individuais: `Medidas individuais são complementares e podem envolver orientação, acolhimento, encaminhamento ao PCMSO, acompanhamento médico ocupacional, apoio psicossocial e capacitação. Não devem ser utilizadas como única resposta quando o fator de risco decorre de falhas organizacionais, gestão inadequada, excesso de demanda ou ambiente de trabalho hostil.`,
  plano: `O plano de ação apresentado neste relatório organiza as medidas por dimensão/fator, prioridade, responsável, prazo sugerido e evidências esperadas. Recomenda-se reavaliação em ciclos de 30, 60, 90 ou 180 dias conforme classificação do risco e criticidade identificada.`,
  anexos: `Integram este documento, quando disponíveis: base consolidada de avaliações, quadro por GHE/setor/função, consolidação por dimensão psicossocial, matriz de risco AIHA, plano de ação, registros de revisão técnica e evidências documentais apresentadas pela empresa.`,
  referencias: [
    "BRASIL. Ministério do Trabalho e Emprego. Norma Regulamentadora nº 01 – Disposições Gerais e Gerenciamento de Riscos Ocupacionais.",
    "BRASIL. Ministério do Trabalho e Emprego. Norma Regulamentadora nº 17 – Ergonomia.",
    "Fundacentro e publicações técnicas aplicáveis à gestão de riscos ocupacionais e ergonomia.",
    "Critérios técnicos de matriz de risco e priorização de medidas preventivas adotados pela Sollute Ocupacional.",
  ],
  termo_lgpd: `Este documento possui finalidade técnica e preventiva, devendo ser utilizado para gestão coletiva dos fatores de riscos psicossociais e integração ao PGR. As informações consolidadas não devem expor dados clínicos individuais, diagnósticos, CID, medicações, histórico pessoal ou informações sensíveis individualizadas.

A interpretação deve ser realizada pelo responsável técnico, considerando a metodologia aplicada, a realidade do trabalho, entrevistas, observações de campo, indicadores ocupacionais, evidências de implantação e limites próprios de uma Avaliação Ergonômica Preliminar.`,
};

/** Matriz P×S used to derive classification when needed in the report. */
export const PXS_MATRIX = [
  ["P1", ["Baixo",    "Baixo",     "Moderado", "Moderado"]],
  ["P2", ["Baixo",    "Moderado",  "Moderado", "Alto"]],
  ["P3", ["Moderado", "Moderado",  "Alto",     "Crítico"]],
  ["P4", ["Moderado", "Alto",      "Crítico",  "Crítico"]],
];

/** Map AIHA level → bg class used in the matrix cells. */
export const MATRIX_BG = {
  "Baixo":    "risk-1",
  "Moderado": "risk-3",
  "Alto":     "risk-4",
  "Crítico":  "risk-5",
};

/** Derive (P, S) coordinates in the matrix from assessment results.
 *  P (1-4): probability — based on overall consolidated score on a 1-5 Likert scale.
 *  S (1-4): severity — escalates with critical answers count and worst dimension intensity.
 */
export function deriveProbabilityIndex(overallScore) {
  if (overallScore < 2) return 1;
  if (overallScore < 3) return 2;
  if (overallScore < 4) return 3;
  return 4;
}

/** Threshold below which the consolidated overall score is considered "good".
 * When the overall average is at or below this value, isolated critical answers
 * stop inflating severity (S) — this is the "bypass inteligente" requested by
 * users who reported the matrix marking 'Alto' even when answers were globally
 * positive. Standard AIHA behaviour kicks back in once the average rises above
 * the Tolerável band. */
export const GOOD_OVERALL_THRESHOLD = 2.5;

export function deriveSeverityIndex(worstDimensionScore, criticalCount, totalResponses, overallScore = null, strict = false) {
  // Severity is anchored by the worst dimension and amplified by critical answers ratio.
  // BYPASS INTELIGENTE: if the overall consolidated score is at/below the Tolerável
  // threshold, we mute the critical-ratio bump AND cap the base severity so a single
  // bad item cannot push the whole company into S4 = Crítica.
  // When `strict=true`, the user opted out of the bypass and the original AIHA rule
  // applies as-is (any critical answer can drive S to 4).
  const good = !strict && overallScore !== null && overallScore <= GOOD_OVERALL_THRESHOLD;
  let base = worstDimensionScore || 0;
  if (good) base = Math.min(base, 3.4); // cap at Substancial-band ceiling → S ≤ 3
  const critRatio = totalResponses > 0 ? criticalCount / Math.max(totalResponses, 1) : 0;
  const bump = !good && critRatio >= 0.10 ? 1 : 0;
  let s;
  if (base < 2) s = 1;
  else if (base < 3) s = 2;
  else if (base < 4) s = 3;
  else s = 4;
  return Math.min(4, s + bump);
}

export function matrixClassification(p, s) {
  return PXS_MATRIX[p - 1]?.[1]?.[s - 1] || "Baixo";
}

/** Used to give a verbal conclusion in the executive summary. */
export function buildConclusao(overallLabel, score, critical, focoDimensao) {
  const lvl = overallLabel || "Sem dados";
  const score100 = Math.round((Number(score) / 5) * 100);
  return `A consolidação indica classificação ${lvl}, com risco médio de ${score100}% e ${critical} resposta(s) crítica(s). O principal foco de gestão identificado é ${focoDimensao || "—"}. Recomenda-se priorizar medidas coletivas e organizacionais, evidenciar a implantação do plano de ação, acompanhar indicadores ocupacionais e reavaliar os fatores psicossociais conforme prazos definidos.`;
}

/* ============================================================
 *  FMEA — Failure Mode and Effects Analysis
 *  RPN = Severidade (1-10) × Ocorrência (1-10) × Detecção (1-10)
 *  Resulting score (1-1000) is bucketed into the 4 classes below.
 * ============================================================ */
export const FMEA_BUCKETS = [
  { max: 50,  label: "Baixo",    cls: "risk-1", description: "Risco residual aceitável — manter monitoramento periódico." },
  { max: 125, label: "Moderado", cls: "risk-3", description: "Requer ações preventivas no plano de gestão regular." },
  { max: 250, label: "Alto",     cls: "risk-4", description: "Ações corretivas com prazo definido e responsável." },
  { max: 1000, label: "Crítico", cls: "risk-5", description: "Ação imediata necessária — escalar para liderança." },
];

export function fmeaClassify(rpn) {
  for (const b of FMEA_BUCKETS) if (rpn <= b.max) return b;
  return FMEA_BUCKETS[FMEA_BUCKETS.length - 1];
}

/** Derive FMEA components from psychosocial assessment data.
 *  All three scales are 1-10. RPN = S × O × D.
 *
 *  Severidade  — intensidade do pior fator (worst dimension score on Likert 1-5 → 1-10).
 *  Ocorrência  — frequência geral do risco (overall score 1-5 → 1-10).
 *  Detecção    — capacidade de detecção pela empresa. ALTA detecção = baixo D.
 *                Anchored on coverage ratio (respostas/trabalhadores esperados).
 *                Quanto MENOR a cobertura, MAIOR o D (mais difícil detectar).
 */
export function deriveFmea({ overallScore = 0, worstDimensionScore = 0,
                             criticalCount = 0, totalResponses = 0,
                             expectedWorkers = 0, strict = false } = {}) {
  // BYPASS INTELIGENTE: same rule as P×S — once the consolidated average is in
  // the Tolerável band or better, isolated critical answers stop padding S, and
  // the base severity is capped so the whole RPN cannot land in "Alto/Crítico"
  // purely because of one outlier item.
  // When `strict=true`, the user opted out of the bypass and the original FMEA
  // calculation applies (single criticals can drive S high).
  const good = !strict && overallScore > 0 && overallScore <= GOOD_OVERALL_THRESHOLD;
  const sevAnchor = worstDimensionScore || overallScore || 0;
  const sevRaw = (good ? Math.min(sevAnchor, 3.4) : sevAnchor) * 2;
  const occRaw = (overallScore || 0) * 2;
  // Coverage: 0 (no responses) → D=10 (zero detection); 100%+ → D=1 (perfect detection)
  const coverage = expectedWorkers > 0
    ? Math.min(1, totalResponses / expectedWorkers)
    : (totalResponses > 0 ? 0.5 : 0);
  const detRaw = 10 - 9 * coverage;

  // Critical answer ratio adds severity weight (cap +2). Skipped when bypass kicks in.
  const critRatio = totalResponses > 0 ? criticalCount / totalResponses : 0;
  const sevBump = good ? 0 : Math.min(2, Math.floor(critRatio / 0.1));

  const S = Math.max(1, Math.min(10, Math.round(sevRaw) + sevBump));
  const O = Math.max(1, Math.min(10, Math.round(occRaw)));
  const D = Math.max(1, Math.min(10, Math.round(detRaw)));
  const rpn = S * O * D;
  const bucket = fmeaClassify(rpn);
  return { S, O, D, rpn, bucket, bypass: good };
}

/** Per-dimension FMEA for the breakdown table. */
export function dimensionFmea(d, { criticalCount = 0, totalResponses = 0, expectedWorkers = 0, strict = false } = {}) {
  return deriveFmea({
    overallScore: d.score,
    worstDimensionScore: d.score,
    criticalCount,
    totalResponses: d.responses || totalResponses,
    expectedWorkers,
    strict,
  });
}
